/**
 * React context for the typed {@link ApiClient}.
 *
 * The app creates ONE client (via `createApiClient`) and wraps the tree once:
 *
 *   <ApiClientProvider client={apiClient}>
 *     <App />
 *   </ApiClientProvider>
 *
 * Every query/mutation hook in this package reads the client from this context
 * via {@link useApiClient}, so feature code never threads the client by hand and
 * never issues a fetch directly.
 */

import { createContext, type ReactElement, type ReactNode, useContext } from 'react';
import type { ApiClient } from '../client';

const ApiClientContext = createContext<ApiClient | null>(null);

export interface ApiClientProviderProps {
  client: ApiClient;
  children: ReactNode;
}

/** Provides the singleton {@link ApiClient} to all hooks below it. */
export function ApiClientProvider({ client, children }: ApiClientProviderProps): ReactElement {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Returns the {@link ApiClient} from the nearest {@link ApiClientProvider}.
 *
 * @throws when called outside an {@link ApiClientProvider} — this is a wiring
 * bug, surfaced eagerly rather than failing later with a confusing fetch error.
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error('useApiClient must be used within an <ApiClientProvider>.');
  }
  return client;
}
